import { Chat, Delete, Email, NotificationsOff, RestartAlt } from '@mui/icons-material'
import { Box, Button, Dialog, DialogTitle, IconButton, Stack, Typography } from '@mui/material'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { IconRowSelector } from '../components/atoms/IconRowSelector'
import { InfoList, InfoListLines } from '../components/atoms/InfoList'
import { PageTitle } from '../components/atoms/PageTitle'
import { useApp } from '../hooks/useApp'
import { callAxios } from '../hooks/useAxios'
import { Device, DeviceAlertMode, Firmware, ID } from '../types/models'
import { Dimming } from '../components/atoms/Dimming'
import { FirmwareInfoPortControl } from '../classes/ports/FirmwareInfoPortControl'
import { useDevices } from '../hooks/useDevices'
import { LinkTo } from '../components/atoms/LinkTo'
import { CustomSwitch } from '../components/atoms/CustomSwitch'
import { Text } from '../components/atoms/Text'

interface AlertModeEditProps {
  device: Device
  disabled: boolean
  onUpdate: () => void
}

function AlertModeEdit(props: AlertModeEditProps): React.ReactElement {

  const { device, disabled, onUpdate } = props
  const app = useApp()

  let current
  switch (device.values?.alert) {
    case 'slack':
      current = <Chat/>
      break
    case 'email':
      current = <Email/>
      break
    case 'none':
      current = <NotificationsOff/>
      break
    default:
      current = <>—</>
  }

  const onSetAlert = (mode: DeviceAlertMode) => {
    callAxios<Device>({
      method: 'PATCH',
      url: `/devices/${device.id}`,
    }, { values: { alert: mode } }).then(res => res.success ? onUpdate() : app.addError(res))
  }

  return <Stack direction="row">
    <Box sx={{ transform: 'translateY(4px)' }}>{current}</Box>
    <IconRowSelector
      sx={{ ml: 2 }}
      disabled={disabled}
      icons={[
        {
          icon: <Chat/>,
          title: t('Send a message to the Slack.'),
          action: () => onSetAlert('slack')
        },
        {
          icon: <Email/>,
          title: t('Send email to the owner.'),
          action: () => onSetAlert('email')
        },
        {
          icon: <NotificationsOff/>,
          title: t('Do not send any notification.'),
          action: () => onSetAlert('none')
        },
      ]}
    />
  </Stack>
}

export function AdminDevicePage(): React.ReactElement {
  const [device, setDevice] = useState<Device | null>(null)
  const [firmwares, setFirmawares] = useState<Firmware[]>([])
  const [firmware, setFirmaware] = useState<Firmware | null>(null)
  const [deletable, setDeletable] = useState(false)
  const [selectable, setSelectable] = useState(false)
  const [unclaimable, setUnclaimable] = useState(false)
  const [claimable, setClaimable] = useState(false)
  const [rebootable, setRebootable] = useState(false)
  const [busy, setBusy] = useState(false)
  const [confirmHwid, setConfirmHwid] = useState('')

  const navigate = useNavigate()
  const { id } = useParams()
  const { control } = useDevices()
  const app = useApp()

  const updateDevice = () => {
    callAxios<Device>({
      url: `/devices/${id}`,
    }).then(res => {
      if (res.success) {
        setDevice(res.data)
      }
    })
  }

  const fetchFirmwares = () => {
    callAxios<Firmware[]>({
      url: '/firmware',
    }).then(res => res.success && setFirmawares(res.data))
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => updateDevice(), [])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => fetchFirmwares(), [])

  const onDelete = () => {
    if (confirmHwid !== device?.hw_id) {
      app.addError(t('Please enter correct hardware ID.'))
      return
    }
    setBusy(true)
    setDeletable(false)
    callAxios({
      method: 'DELETE',
      disabled: !deletable,
      url: `/devices/${device && device.id}`
    }).then(() => {
      setBusy(false)
      navigate('/admin/devices')
    })
  }

  const onReboot = async () => {
    const fwInfo = control.ports({ portType: 'FW_INFO', deviceId: device?.id })
    if (fwInfo.length) {
      await fwInfo[0].sendCommand({ name: 'reboot', value: null })
    }
    setRebootable(false)
  }

  const onUnclaim = () => {
    setUnclaimable(false)
    setBusy(true)
    callAxios({
      method: 'DELETE',
      disabled: !unclaimable,
      url: `/devices/claim/${device && device.id}`
    }).then((res) => {
      setBusy(false)
      if (res.success) {
        updateDevice()
      } else {
        app.addError(res)
      }
    })
  }

  const onClaim = () => {
    setClaimable(false)
    setBusy(true)
    callAxios({
      method: 'POST',
      disabled: !claimable,
      url: '/devices/claim'
    }, { hw_id: device?.hw_id }).then((res) => {
      setBusy(false)
      if (res.success) {
        updateDevice()
      } else {
        app.addError(res)
      }
    })
  }

  const onFlash = () => {
    setFirmaware(null)
    setBusy(true)
    app.addInfo(t('Writing firmware started...'))
    callAxios({
      method: 'PUT',
      disabled: !firmware || !device,
      url: `/firmware/devices/${device?.id}`
    }, { version: firmware?.version }).then(res => {
      setBusy(false)
      if (!res.success) {
        app.addError(res)
      } else {
        setFirmaware(null)
      }
    })
  }

  const onToggleFirmwareUpgrades = () => {
    if (!device) return

    const disableUpgrade = !device.values?.disable_upgrade

    callAxios<Device>({
      method: 'PATCH',
      url: `/devices/${device.id}`,
    }, { values: { disable_upgrade: disableUpgrade } }).then(res => res.success ? updateDevice() : app.addError(res))
  }

  const fwPort = control.ports<FirmwareInfoPortControl>({ deviceId: parseInt(`${id}`) as ID, portType: 'FW_INFO' })[0]
  const fwValues = fwPort ? fwPort.port.values : undefined
  const inStock = !device?.owner

  const lines: InfoListLines<Device> = {
    [t('Name')]: 'name',
    [t('Description')]: 'device_spec.description',
    [t('Owner')]: (dev: Device) => <>
        <LinkTo url={`/admin/users/${dev.owner?.id}`} title={dev.owner?.email || ''}/>
        {!inStock &&
          <LinkTo sx={{ ml: 2 }} color="primary.main" underline onClick={() => setUnclaimable(true)} title={t('Unclaim')}/>
        }
        {inStock &&
          <LinkTo sx={{ ml: 2 }} color="primary.main" underline onClick={() => setClaimable(true)} title={t('Claim')}/>
        }
      </>,
    [t('Last Data Received')]: 'values.last_data',
    [t('Alert Mode')]: (dev: Device) => <AlertModeEdit disabled={inStock} device={dev} onUpdate={updateDevice}/>,
    [t('Last Offline Alert')]: 'values.last_offline_alert',
    [t('Disable Firmware Upgrades')]: (dev: Device) => <CustomSwitch checked={dev.values?.disable_upgrade || false} onClick={onToggleFirmwareUpgrades}/>
  }

  const chunks = fwValues?.flag_dfu_started ? (fwValues?.flag_new_protocol ? fwValues?.dfu_n_received_chunks : fwValues?.dfu_first_missing_chunk) : 0

  return <Box>
    <Dimming on={busy}/>
    <PageTitle sx={{ mb: 2 }} title={device ? t('Device') + ': ' + device.hw_id : ''}/>
    {
      fwValues?.flag_dfu_started && <Box sx={{ m: 3, p: 2, borderRadius: 2, border: '1px solid rgba(255,255,255,0.3)' }}>
        <Trans>Firmware Upgrade in Progress</Trans> {t('({chunks} chunks sent)').replace('{chunks}', `${chunks}`)}
      </Box>
    }
    { device && <InfoList lines={lines} item={device}/>}
    <Box sx={{ mt: 3 }}>
      { device && <Button onClick={() => setSelectable(true)} disabled={inStock || fwValues?.flag_dfu_started || device.values?.disable_upgrade}><Trans>Change Firmware</Trans></Button>}
      { device && <Button onClick={() => navigate(`/config/${device.id}`)} sx={{ ml: 2 }}><Trans>View Config</Trans></Button>}
    </Box>

    <Box sx={{ mt: 4, textAlign: 'center' }}>
      <IconButton sx={{ color: 'blue' }} disabled={inStock || !device} onClick={() => setRebootable(true)}><RestartAlt/></IconButton>
    </Box>

    <Box sx={{ mt: 4, textAlign: 'center' }}>
      <IconButton data-cy="icon-Delete device" sx={{ color: 'error.dark' }} disabled={!device} onClick={() => setDeletable(true)}><Delete/></IconButton>
    </Box>

    <Dialog open={!!deletable} onClose={() => setDeletable(false)}>
      <DialogTitle sx={{ backgroundColor: 'error.light' }}><Trans>Delete Device?</Trans></DialogTitle>
      <Box sx={{ p: 2 }}>
        <Typography variant="h2">{device && device.hw_id}</Typography>
        <Trans>Are you sure that you want to delete device?</Trans><> </>
        <Trans>Please enter the device hardware ID below:</Trans><br/>
        <Text
          data-cy="text-Delete device by ID"
          margin="normal"
          sx={{ my: 2 }}
          size="small"
          value={confirmHwid}
          onChange={(e) => setConfirmHwid(e.target.value)}
        />

        <Stack direction="row" spacing={2} sx={{ m: 2 }}>
          <Button color="primary" onClick={() => setDeletable(false)}><Trans>Cancel</Trans></Button>
          <Button data-cy="button-Delete device OK" color="secondary" onClick={onDelete}><Trans>OK</Trans></Button>
        </Stack>
      </Box>
    </Dialog>

    <Dialog open={!!rebootable} onClose={() => setRebootable(false)}>
      <DialogTitle sx={{ backgroundColor: 'blue' }}><Trans>Reboot Device?</Trans></DialogTitle>
      <Box sx={{ p: 2 }}>
        <Typography variant="h2">{device && device.hw_id}</Typography>
        <Trans>Are you sure that you want to reboot device?</Trans>
        <Stack direction="row" spacing={2} sx={{ m: 2 }}>
          <Button color="primary" onClick={() => setRebootable(false)}><Trans>Cancel</Trans></Button>
          <Button color="secondary" onClick={onReboot}><Trans>OK</Trans></Button>
        </Stack>
      </Box>
    </Dialog>

    <Dialog open={!!unclaimable} onClose={() => setUnclaimable(false)}>
      <DialogTitle sx={{ backgroundColor: 'success.light', color: 'black' }}><Trans>Unclaim Device?</Trans></DialogTitle>
      <Box sx={{ p: 2 }}>
        <Typography variant="h2">{device && device.hw_id}</Typography>
        <Trans>Are you sure that you want to restore device back to the stock?</Trans>
        <Stack direction="row" spacing={2} sx={{ m: 2 }}>
          <Button color="primary" onClick={() => setUnclaimable(false)}><Trans>Cancel</Trans></Button>
          <Button data-cy="button-Unclaim OK" color="secondary" onClick={onUnclaim}><Trans>OK</Trans></Button>
        </Stack>
      </Box>
    </Dialog>

    <Dialog open={!!claimable} onClose={() => setClaimable(false)}>
      <DialogTitle sx={{ backgroundColor: 'success.light', color: 'black' }}><Trans>Claim Device?</Trans></DialogTitle>
      <Box sx={{ p: 2 }}>
        <Typography variant="h2">{device && device.hw_id}</Typography>
        <Trans>Are you sure that you want to claim this device?</Trans>
        <Stack direction="row" spacing={2} sx={{ m: 2 }}>
          <Button color="primary" onClick={() => setClaimable(false)}><Trans>Cancel</Trans></Button>
          <Button color="secondary" onClick={onClaim}><Trans>OK</Trans></Button>
        </Stack>
      </Box>
    </Dialog>

    <Dialog open={!!selectable} onClose={() => setSelectable(false)}>
      <DialogTitle sx={{ backgroundColor: 'success.dark' }}><Trans>Change Firmware?</Trans></DialogTitle>
      <Box sx={{ p: 2 }}>
        <Typography variant="h2">{device && device.hw_id}</Typography>
        <Trans>Select the firmware version from the below options</Trans>:
        <Box sx={{ display: 'flex' }}>
          {
            firmwares.filter(fw => fw.available).map((fw, idx) =>
              <Box
                sx={{
                  m: 1,
                  p: 1,
                  color: 'primary.main',
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  cursor: 'pointer'
                }}
                key={idx}
                onClick={
                  () => {
                    setSelectable(false)
                    setFirmaware(fw)
                  }
                }
              >
                {fw.version}
              </Box>
            )
          }
        </Box>
        <Stack direction="row" spacing={2} sx={{ m: 2 }}>
          <Button color="primary" onClick={() => setSelectable(false)}><Trans>Cancel</Trans></Button>
        </Stack>
      </Box>
    </Dialog>

    <Dialog open={!!firmware} onClose={() => setFirmaware(null)}>
      <DialogTitle sx={{ backgroundColor: 'success.dark' }}><Trans>Change Firmware?</Trans></DialogTitle>
      <Box sx={{ p: 2 }}>
        <Typography variant="h2">{firmware?.version} ➡ {device?.hw_id}</Typography>
        <Trans>Are you sure that you want to change firmware on device?</Trans>
        <Stack direction="row" spacing={2} sx={{ m: 2 }}>
          <Button color="primary" onClick={() => setFirmaware(null)}><Trans>Cancel</Trans></Button>
          <Button color="secondary" onClick={onFlash}><Trans>OK</Trans></Button>
        </Stack>
      </Box>
    </Dialog>
  </Box>
}
